import React, { useState, useEffect } from 'react';
import { FiX, FiCheck, FiPackage, FiTruck, FiMapPin, FiCalendar, FiClock, FiChevronRight } from 'react-icons/fi';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { cn } from '../../../utils/cn';

// Custom CSS for animations (injected dynamically or could be in index.css)
const animationStyles = `
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes lineGrow {
    from { height: 0; }
    to { height: 100%; }
  }
  .animate-slide-up { animation: slideUp 0.5s ease-out forwards; }
  .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
  .animate-line-grow { animation: lineGrow 0.5s ease-out forwards; }
`;

const StatusIcon = ({ status, isActive, isCompleted, icon }) => {
  return (
    <div className={cn(
      "relative z-10 w-12 h-12 rounded-2xl flex items-center justify-center text-xl shadow-sm transition-all duration-500",
      isActive ? "bg-primary text-white scale-110 shadow-primary/30 shadow-lg ring-4 ring-primary/10" :
        isCompleted ? "bg-green-500 text-white" :
          "bg-gray-100 text-gray-400"
    )}>
      {icon || <FiPackage />}
      {isActive && (
        <span className="absolute -right-1 -top-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white animate-pulse" />
      )}
    </div>
  );
};

const TimelineItem = ({ step, index, currentStep, isLast }) => {
  const isCompleted = index < currentStep - 1; // 1-based IDs assumed
  const isActive = index === currentStep - 1;

  // Stagger animation delay based on index
  const delayStyle = { animationDelay: `${index * 100}ms` };

  return (
    <div
      className="relative flex gap-8 animate-slide-up group"
      style={delayStyle}
    >
      {/* Connector Line */}
      {!isLast && (
        <div className="absolute left-[1.5rem] top-12 bottom-0 w-px -mb-6 z-0">
          {/* Background Line (guide) */}
          <div className="absolute inset-0 w-full h-full bg-gray-200" />
          {/* Progress Line */}
          <div
            className={cn(
              "absolute top-0 left-0 w-full bg-primary transition-all duration-700 ease-out",
              (isCompleted) ? "h-full" : isActive ? "h-1/2 bg-gradient-to-b from-primary to-transparent" : "h-0"
            )}
          />
        </div>
      )}

      {/* Icon Column */}
      <div className="relative flex-shrink-0 z-10 py-1">
        <div className={cn(
          "w-12 h-12 rounded-full flex items-center justify-center text-xl border-4 transition-all duration-500",
          isActive ? "bg-white border-primary text-primary shadow-lg shadow-primary/20 scale-110" :
            isCompleted ? "bg-primary border-primary text-white" :
              "bg-white border-gray-200 text-gray-300"
        )}>
          {isCompleted ? <FiCheck size={20} /> : step.icon || <FiPackage size={20} />}
        </div>
      </div>

      {/* Content Column */}
      <div className={cn(
        "flex-grow pb-8 transition-all duration-300",
        isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100"
      )}>
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 mb-1.5">
          <h4 className={cn(
            "font-bold text-lg",
            isActive ? "text-gray-900" : "text-gray-500"
          )}>
            {step.title}
          </h4>
          <span className="text-sm font-medium text-gray-400 flex items-center gap-1">
            <FiClock size={12} />
            {step.date}
          </span>
        </div>

        <p className="text-gray-600 text-base mb-2">
          {step.location}
        </p>

        {isActive && (
          <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mt-1 animate-scale-in origin-left">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
            </span>
            Kargo burada işleniyor
          </div>
        )}
      </div>
    </div>
  );
};

export const TrackingModal = ({ data, onClose }) => {
  // Local state for debugging purposes to simulate step changes
  const [debugStep, setDebugStep] = useState(data.steps.findIndex(s => s.id === data.currentStep) + 1);

  // Reset debug step if modal re-opens with new data
  useEffect(() => {
    setDebugStep(data.steps.findIndex(s => s.id === data.currentStep) + 1);
  }, [data.currentStep, data.steps]);

  const handleNextStep = () => {
    setDebugStep(prev => (prev < data.steps.length ? prev + 1 : 1));
  };

  const currentStepData = data.steps[debugStep - 1] || data.steps[0];

  return (
    <Modal open={true} onClose={onClose} size="xl">
      <style>{animationStyles}</style>

      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-3">
          <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-100 text-primary">
            <FiPackage size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Kargo Takip detayları</h3>
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>No:</span>
              <span className="font-mono font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{data.trackingNumber}</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700"
        >
          <FiX size={24} />
        </button>
      </div>

      <div className="p-0 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 h-[75vh] md:h-auto overflow-y-auto">

        {/* Left Column: Status Summary */}
        <div className="md:col-span-1 flex flex-col">
          {/* Top: Estimated Delivery */}
          <div className="mb-6 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
            <div className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-1">Tahmini Teslimat</div>
            <div className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FiCalendar className="text-blue-500" />
              {data.estimatedDelivery}
            </div>
          </div>

          {/* Center: Current Status */}
          <div className="flex flex-col items-center justify-center text-center animate-scale-in my-2">
            <div className="w-24 h-24 rounded-full bg-primary/5 flex items-center justify-center text-primary mb-6 ring-8 ring-primary/5 transition-all duration-500">
              {/* Dynamic Icon based on current step */}
              <span className="text-4xl">
                {currentStepData.icon || <FiTruck />}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{currentStepData.title}</h2>
            <p className="text-gray-500 leading-relaxed max-w-[200px]">{currentStepData.location}</p>
          </div>

          {/* Bottom: Notes Section */}
          <div className="mt-6">
            <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-xl">
              <h4 className="font-bold text-red-700 mb-1 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                NOTLAR
              </h4>
              <p className="text-sm text-red-600/80 leading-snug">
                Kargonuz gümrük işlemleri nedeniyle kısa bir süre bekletilmektedir.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column: Timeline (Minimalist) */}
        <div className="md:col-span-2 pl-0 md:pl-6 md:pr-24 md:border-l border-gray-100 flex items-start">
          <div className="py-4 w-full">
            {data.steps.map((step, index) => (
              <TimelineItem
                key={step.id}
                step={step}
                index={index}
                currentStep={debugStep}
                isLast={index === data.steps.length - 1}
              />
            ))}
          </div>
        </div>

      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
        {/* Debug Button */}
        <button
          type="button"
          onClick={handleNextStep}
          className="text-xs font-mono text-gray-500 hover:text-primary hover:bg-white px-3 py-1.5 rounded-lg border border-transparent hover:border-gray-200 transition-all flex items-center gap-2"
          title="Sadece geliştirme amaçlıdır"
        >
          <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
          Debug: Sonraki Adım
          <FiChevronRight />
        </button>
      </div>

    </Modal>
  );
};

